import React from 'react'
import { goalsDraft } from '../../plan/goalsDraft'
import { PROFILES, checkGoalFields, goalFields, goalFromFields, type GoalDraft, type GoalFieldErrors, type GoalFields } from '../../plan/planForm'

// holidayDays says weeks off in days, on a five-day week: "That's about 30
// working days." Empty for an answer that isn't a number yet.
export function holidayDays(weeks: string): string {
  const w = Number(weeks)
  if (weeks.trim() === '' || !Number.isFinite(w) || w < 0) return ''
  if (w === 0) return 'No time off planned.'
  const days = Math.round(w * 5)
  return `That’s about ${days} working ${days === 1 ? 'day' : 'days'}, on a five-day week.`
}

// useGoalFields holds the goal's fields as typed. Every change is kept as a
// setup draft, so closing the app half-way loses nothing. mode gates which
// draft is read: editing an existing goal never picks up a first-time draft.
export function useGoalFields(initial: GoalDraft | null, mode: 'first' | 'edit') {
  const [fields, setFields] = React.useState<GoalFields>(() => goalsDraft.setup(mode)?.fields ?? goalFields(initial))
  const [errors, setErrors] = React.useState<GoalFieldErrors>({})

  const set = (change: Partial<GoalFields>) => {
    setFields((f) => {
      const next = { ...f, ...change }
      goalsDraft.saveSetup({ fields: next, mode })
      return next
    })
    // A field she is fixing stops complaining straight away.
    setErrors((e) => {
      const next = { ...e }
      for (const key of Object.keys(change) as (keyof GoalFields)[]) delete next[key]
      return next
    })
  }

  // submit shows every problem at once and returns the goal only when there are none.
  const submit = (): GoalDraft | null => {
    const found = checkGoalFields(fields)
    setErrors(found)
    const goal = goalFromFields(fields)
    if (goal) goalsDraft.clearSetup()
    return goal
  }

  const reset = () => {
    goalsDraft.clearSetup()
    setFields(goalFields(initial))
    setErrors({})
  }

  return { fields, errors, set, submit, reset }
}

interface Props {
  fields: GoalFields
  errors: GoalFieldErrors
  onChange: (change: Partial<GoalFields>) => void
}

// GoalFieldsForm is the goal on one card: what she wants to take home, her
// costs, her time off and how she works. Days and hours are not asked here:
// the week she paints answers those, so they can never contradict it.
export default function GoalFieldsForm({ fields, errors, onChange }: Props) {
  const days = holidayDays(fields.holidays)
  return (
    <div className="goals-fields">
      <label className="goals-field">
        <span className="goals-question">Take home each month</span>
        <span className="goals-hint">Before tax, after your business costs.</span>
        <input inputMode="decimal" placeholder="£3,000" value={fields.target} aria-invalid={errors.target ? true : undefined} onChange={(e) => onChange({ target: e.target.value })} />
        {errors.target && <span className="field-error">{errors.target}</span>}
      </label>

      <label className="goals-field">
        <span className="goals-question">Business costs each month</span>
        <span className="goals-hint">Room rent, products, insurance. Leave it empty if you’re not sure.</span>
        <input inputMode="decimal" placeholder="£400" value={fields.costs} aria-invalid={errors.costs ? true : undefined} onChange={(e) => onChange({ costs: e.target.value })} />
        {errors.costs && <span className="field-error">{errors.costs}</span>}
      </label>

      {/* Weeks are what the plan needs, but hard to picture, so she can tap a
          common answer and sees her answer in days too. */}
      <div className="goals-field">
        <span className="goals-question">Time off in a year</span>
        <span className="goals-hint">
          Count summer, Christmas, bank holidays and long weekends. Most people in the UK take 5 to 6 weeks.
        </span>
        <div className="chip-group goals-holiday-picks" role="group" aria-label="Common amounts">
          {[2, 4, 6, 8].map((w) => (
            <button key={w} type="button" className="chip" aria-pressed={fields.holidays === String(w)} onClick={() => onChange({ holidays: String(w) })}>
              {w} weeks
            </button>
          ))}
        </div>
        <div className="goals-holiday-input">
          <input
            inputMode="numeric"
            placeholder="6"
            aria-label="Weeks a year"
            value={fields.holidays}
            aria-invalid={errors.holidays ? true : undefined}
            onChange={(e) => onChange({ holidays: e.target.value })}
          />
          <span aria-hidden="true">weeks a year</span>
        </div>
        {days && <span className="goals-hint">{days}</span>}
        {errors.holidays && <span className="field-error">{errors.holidays}</span>}
      </div>

      <fieldset className="goals-profiles">
        <legend className="goals-question">Which sounds most like you?</legend>
        {PROFILES.map((p) => (
          <label key={p.id} className={`goals-profile${fields.profile === p.id ? ' selected' : ''}`}>
            <input type="radio" name="profile" value={p.id} checked={fields.profile === p.id} onChange={() => onChange({ profile: p.id })} />
            <span className="goals-profile__label">{p.label}</span>
            <span className="goals-hint">{p.blurb}</span>
          </label>
        ))}
        {errors.profile && <span className="field-error">{errors.profile}</span>}
      </fieldset>
    </div>
  )
}
